import { createPopper, type Instance, type Placement, type PositioningStrategy, type VirtualElement } from '@popperjs/core'
import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface PopperProps {
  open: boolean
  anchorEl?: Element | VirtualElement | null
  placement?: Placement
  disablePortal?: boolean
  keepMounted?: boolean
  transition?: boolean
  strategy?: PositioningStrategy
  modifiers?: Record<string, unknown>[]
  popperOptions?: {
    placement?: Placement
    strategy?: PositioningStrategy
    modifiers?: Record<string, unknown>[]
  }
  className?: string
  style?: React.CSSProperties
  contentEditable?: boolean
  onKeyDown?: (event: React.KeyboardEvent) => void
  children?: ReactNode | ((props: { TransitionProps?: { in?: boolean; onEnter?: () => void; onExited?: () => void } }) => ReactNode)
}

export const Popper = forwardRef<HTMLDivElement, PopperProps>(
  (
    {
      open,
      anchorEl,
      placement = 'bottom',
      disablePortal = false,
      keepMounted = false,
      transition = false,
      strategy,
      modifiers,
      popperOptions,
      className,
      style,
      contentEditable,
      onKeyDown,
      children,
    },
    ref
  ) => {
    const popperRef = useRef<HTMLDivElement | null>(null)
    const instanceRef = useRef<Instance | null>(null)
    const [exited, setExited] = useState(!open)

    // Combine refs
    const setRefs = (node: HTMLDivElement | null) => {
      popperRef.current = node
      if (typeof ref === 'function') {ref(node)}
      else if (ref) {ref.current = node}
    }

    useEffect(() => {
      if (!open || !anchorEl || !popperRef.current) {
        instanceRef.current?.destroy()
        instanceRef.current = null
        return
      }

      const resolvedPlacement = popperOptions?.placement ?? placement
      const resolvedStrategy = popperOptions?.strategy ?? strategy ?? 'absolute'
      const resolvedModifiers = popperOptions?.modifiers ?? modifiers ?? []

      instanceRef.current = createPopper(anchorEl as Element, popperRef.current, {
        placement: resolvedPlacement,
        strategy: resolvedStrategy,
        modifiers: resolvedModifiers as never[],
      })

      return () => {
        instanceRef.current?.destroy()
        instanceRef.current = null
      }
    }, [open, anchorEl, placement, strategy, modifiers, popperOptions])

    if (!keepMounted && !open && exited) {
      return null
    }

    const transitionProps = transition
      ? {
          in: open,
          onEnter: () => setExited(false),
          onExited: () => setExited(true),
        }
      : undefined

    const content = (
      <div
        ref={setRefs}
        role="tooltip"
        className={className}
        style={{
          position: strategy ?? popperOptions?.strategy ?? 'absolute',
          zIndex: 1300,
          ...style,
        }}
        contentEditable={contentEditable}
        onKeyDown={onKeyDown}
      >
        {typeof children === 'function' ? children({ TransitionProps: transitionProps }) : children}
      </div>
    )

    if (disablePortal) {
      return content
    }

    return createPortal(content, document.body)
  }
)

Popper.displayName = 'Popper'
